import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiService } from '../services/apiService';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import VirtualCallList from '../components/VirtualCallList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const SimplifiedManualCalls = () => {
  // Use infinite scroll for calls with memory limit for large datasets
  const {
    data: callsData,
    loading: loadingCalls,
    hasMore: hasMoreCalls,
    observerTarget: callsObserverTarget,
    reset: resetCalls
  } = useInfiniteScroll(
    async (page, limit) => {
      const response = await apiService.calls.getAll({ page, limit });
      return response;
    },
    {
      initialPage: 1,
      pageSize: 20,
      enabled: true,
      maxItemsInMemory: 1000 // Keep only last 1000 items in memory for 50k+ records
    }
  );

  const calls = callsData || [];
  const [phone, setPhone] = useState('+33756834054');
  const [script, setScript] = useState('Hello! I am calling you on behalf of VoxiPro AI to present our revolutionary voice automation solution with artificial intelligence. Our platform allows you to automate your prospecting calls with ultra-realistic conversational AI. How are you today?');
  const [loading, setLoading] = useState(false);
  const [callInProgress, setCallInProgress] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  // Language selection - must be selected first
  const [selectedLanguage, setSelectedLanguage] = useState('');

  // AI Configuration - empty by default, user must select
  const [aiConfig, setAiConfig] = useState({
    stt_model: '',
    llm_model: '',
    tts_model: '',
    voice: '',
    tts_speed: 1.0,
    system_prompt: '',
    language: '' // Explicit language field
  });

  // State for sound ambiance
  const [backgroundAmbiance, setBackgroundAmbiance] = useState('none');
  const [availableAmbiances, setAvailableAmbiances] = useState([]);

  // AMD Configuration - matching DEFAULT_AMD_CONFIG from CampaignsPage.js
  const [amdConfig, setAmdConfig] = useState({
    enabled: false,
    initial_silence: 2500,
    greeting: 1500,
    after_greeting_silence: 800,
    total_analysis_time: 5000,
    min_word_length: 100,
    between_words_silence: 50,
    maximum_number_of_words: 2,
    silence_threshold: 256,
    maximum_word_length: 5000
  });

  // Available models - loaded from API
  const [availableModels, setAvailableModels] = useState({
    tts: [],
    stt: [],
    llm: [],
    voices: []
  });

  // Available voices for selected TTS
  const [availableVoices, setAvailableVoices] = useState([]);

  // Configuration SIP
  const [sipConfig, setSipConfig] = useState({
    provider: 'flashcall',
    caller_id: 'VoxiPro_Manual'
  });

  // Available SIP providers - loaded from API
  const [availableSipProviders, setAvailableSipProviders] = useState([
    { id: 'flashcall', name: 'Flashcall', status: 'available' } // Temporary fallback
  ]);

  // Load SIP providers from API
  const loadSipProviders = async () => {
    try {
      const response = await apiService.sip.getProfiles();
      const providers = response.data.map(profile => ({
        id: profile.name.toLowerCase().replace(/\s+/g, '_'),
        name: profile.name,
        status: profile.enabled ? 'available' : 'disabled',
        enabled: profile.enabled
      }));
      setAvailableSipProviders(providers);
    } catch (error) {
      console.error('Error loading SIP providers:', error);
    }
  };

  useEffect(() => {
    loadSipProviders();
    loadAvailableAmbiances();
  }, []);

  // Load models from API on component mount
  useEffect(() => {
    const loadAvailableModels = async () => {
      try {
        const response = await apiService.aiModels.getAvailable();
        const data = response.data;

        // Group models by type - CORRECTION: data.models is an object
        const modelsByType = {
          tts: [],
          stt: [],
          llm: [],
          voices: [
            // Keep default voices
            { id: 'conversational_call', name: 'Conversational Voice', status: 'available' },
            { id: 'professional_call', name: 'Professional Voice', status: 'available' },
            { id: 'friendly_call', name: 'Friendly Voice', status: 'available' }
          ]
        };

        // CORRECTION: data.models is an object with keys tts, stt, llm
        if (data.models && typeof data.models === 'object') {
          // Load TTS models
          if (data.models.tts && Array.isArray(data.models.tts)) {
            modelsByType.tts = data.models.tts.map(model => ({
              id: model.id,
              name: model.name || model.id,
              status: model.status || 'available',
              language: model.language || null // Preserve language property for filtering
            }));
          }

          // Load STT models  
          if (data.models.stt && Array.isArray(data.models.stt)) {
            modelsByType.stt = data.models.stt.map(model => ({
              id: model.id,
              name: model.name || model.id,
              status: model.status || 'available',
              language: model.language || null // Preserve language property for filtering
            }));
          }

          // Load LLM models
          if (data.models.llm && Array.isArray(data.models.llm)) {
            modelsByType.llm = data.models.llm.map(model => ({
              id: model.id,
              name: model.name || model.id,
              status: model.status || 'available',
              language: model.language || null // Preserve language property for filtering
            }));
          }

          // Load additional voices
          if (data.models.voices && Array.isArray(data.models.voices)) {
            data.models.voices.forEach(voice => {
              modelsByType.voices.push({
                id: voice.id,
                name: voice.name || voice.id,
                status: voice.status || 'available'
              });
            });
          }
        }

        setAvailableModels(modelsByType);
        console.log('✅ Models loaded from API:', modelsByType);
      } catch (error) {
        console.error('❌ Error loading models:', error);
        // Default models remain active
      }
    };

    loadAvailableModels();
  }, []);

  // Filter models by selected language
  const getFilteredModels = (modelType) => {
    if (!selectedLanguage) return [];
    return availableModels[modelType]?.filter(model => {
      // PRIORITY 1: Check if model has explicit language property (most reliable)
      if (model.language) {
        return model.language === selectedLanguage;
      }

      // PRIORITY 2: For STT models, check known multi-language models
      if (modelType === 'stt') {
        const multiLanguageSTT = ['faster_whisper_large_v3', 'whisper_tiny'];
        const modelIdLower = model.id.toLowerCase();
        if (multiLanguageSTT.some(stt => modelIdLower.includes(stt))) {
          // These models support both languages, but should be filtered by language property
          // If no language property, include for both (fallback)
          return true;
        }
      }

      // PRIORITY 3: For LLM models, check known multi-language models
      if (modelType === 'llm') {
        const multiLanguageLLM = ['lucie_7b', 'qwen2_15b'];
        const modelIdLower = model.id.toLowerCase();
        if (multiLanguageLLM.some(llm => modelIdLower.includes(llm))) {
          // These models support both languages, but should be filtered by language property
          // If no language property, include for both (fallback)
          return true;
        }
      }

      // PRIORITY 4: For TTS models, check known French TTS models (Piper supports both but primarily French)
      if (modelType === 'tts' && selectedLanguage === 'fr') {
        const frenchTTSModels = ['gtts', 'french_tts', 'piper', 'chatterbox'];
        const modelIdLower = model.id.toLowerCase();
        if (frenchTTSModels.some(tts => modelIdLower.includes(tts))) {
          return true;
        }
      }

      // PRIORITY 5: For TTS models, check known English TTS models (Piper supports both but primarily French)
      if (modelType === 'tts' && selectedLanguage === 'en') {
        const englishTTSModels = ['kokoro', 'piper', 'chatterbox'];
        const modelIdLower = model.id.toLowerCase();
        if (englishTTSModels.some(tts => modelIdLower.includes(tts))) {
          return true;
        }
      }

      // PRIORITY 6: Check model ID for language suffix
      if (selectedLanguage === 'en') {
        return model.id.includes('_en') || (!model.id.includes('_fr') && !model.id.includes('_de') && !model.id.includes('_es'));
      }
      if (selectedLanguage === 'fr') {
        return model.id.includes('_fr');
      }
      return true;
    }) || [];
  };

  // Reset AI config when language changes
  useEffect(() => {
    if (selectedLanguage) {
      setAiConfig(prev => ({
        ...prev,
        stt_model: '',
        llm_model: '',
        tts_model: '',
        voice: '',
        language: selectedLanguage
      }));
      setAvailableVoices([]);
    }
  }, [selectedLanguage]);

  // Load available voices when TTS model changes
  useEffect(() => {
    const loadVoicesForTTS = async () => {
      if (aiConfig.tts_model) {
        try {
          const response = await apiService.voices.getByProvider(aiConfig.tts_model);
          const voices = response.data || [];
          setAvailableVoices(voices);

          // Reset voice if it's no longer available
          if (voices.length > 0 && !voices.find(voice => voice.id === aiConfig.voice)) {
            setAiConfig(prev => ({ ...prev, voice: voices[0].id }));
          } else if (voices.length === 0) {
            // Clear voice selection if no voices available
            setAiConfig(prev => ({ ...prev, voice: '' }));
          }
        } catch (error) {
          console.error('Error loading voices for TTS:', error);
          setAvailableVoices([]);
          setAiConfig(prev => ({ ...prev, voice: '' }));
        }
      } else {
        setAvailableVoices([]);
        setAiConfig(prev => ({ ...prev, voice: '' }));
      }
    };

    loadVoicesForTTS();
  }, [aiConfig.tts_model]);

  // Function to reload models
  const refreshModels = () => {
    // Trigger a model reload
    setAvailableModels(prev => ({ ...prev })); // Force re-render
  };

  // Load available ambiances
  const loadAvailableAmbiances = async () => {
    try {
      const response = await apiService.ambiances.getAvailable();
      console.log('✅ Loaded ambiances from API:', response.data);
      if (response.data && response.data.ambiances) {
        setAvailableAmbiances(response.data.ambiances);
        console.log(`✅ Set ${response.data.ambiances.length} ambiances`);
      } else {
        console.warn('⚠️ API response missing ambiances array, using fallback');
        setAvailableAmbiances([
          { id: 'none', name: 'No Ambiance', description: 'No background ambiance' }
        ]);
      }
    } catch (error) {
      console.error('❌ Error loading ambiances from API:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback with default ambiances - but this should not be needed if API works
      setAvailableAmbiances([
        { id: 'none', name: 'No Ambiance', description: 'No background ambiance' }
      ]);
    }
  };
  const [conversation, setConversation] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Connected');
  const [activeCallId, setActiveCallId] = useState(null);
  const [conversationPolling, setConversationPolling] = useState(null);

  // Call Recordings State
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [currentPlayingRecording, setCurrentPlayingRecording] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);

  // Refs and state for height matching
  const formContainerRef = useRef(null);
  const callHistoryRef = useRef(null);
  const [formHeight, setFormHeight] = useState(null);

  useEffect(() => {
    loadCalls();
    loadRecordings();
    // Refresh recordings every 30 seconds
    const interval = setInterval(loadRecordings, 30000);
    return () => clearInterval(interval);
  }, []);

  // Measure form height and apply to Call History
  useEffect(() => {
    const measureFormHeight = () => {
      if (formContainerRef.current) {
        const height = formContainerRef.current.getBoundingClientRect().height;
        setFormHeight(height);
      }
    };

    // Initial measurement
    measureFormHeight();

    // Use ResizeObserver to handle dynamic changes
    let resizeObserver;
    if (formContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        measureFormHeight();
      });
      resizeObserver.observe(formContainerRef.current);
    }

    // Fallback: also listen to window resize
    window.addEventListener('resize', measureFormHeight);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureFormHeight);
    };
  }, []);

  // Polling to retrieve real-time conversation
  const startConversationPolling = (callId) => {
    console.log('🔄 Starting conversation polling for:', callId);
    setActiveCallId(callId);

    const pollConversation = async () => {
      try {
        const response = await apiService.calls.getConversation(callId);
        if (response.data.success && response.data.messages.length > 0) {
          setConversation(response.data.messages.map(msg => ({
            type: msg.type,
            text: msg.text,
            timestamp: new Date(msg.timestamp)
          })));
        }
      } catch (error) {
        console.warn('Error polling conversation:', error);
      }
    };

    // Polling every 2 seconds
    const interval = setInterval(pollConversation, 2000);
    setConversationPolling(interval);

    // First immediate call
    pollConversation();
  };

  const stopConversationPolling = () => {
    if (conversationPolling) {
      console.log('⏹️ Stopping conversation polling');
      clearInterval(conversationPolling);
      setConversationPolling(null);

      // Clean up conversation on backend
      if (activeCallId) {
        apiService.calls.deleteConversation(activeCallId).catch(console.warn);
      }
      setActiveCallId(null);
    }
  };

  // Clean up polling on component unmount
  useEffect(() => {
    return () => {
      stopConversationPolling();
    };
  }, []);

  // Calls are loaded via infinite scroll hook
  // This function is kept for backward compatibility but does nothing
  const loadCalls = async () => {
    // Calls are automatically loaded by the infinite scroll hook
  };

  const loadRecordings = async () => {
    try {
      setLoadingRecordings(true);
      const response = await apiService.recordings.getAll();
      setRecordings(response.data.recordings || []);
    } catch (error) {
      console.error('Error loading recordings:', error);
      toast.error('Error loading recordings: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingRecordings(false);
    }
  };

  const playRecording = (recording) => {
    if (!recording || !recording.url) {
      toast.error('Recording URL is not available');
      return;
    }

    const audioUrl = `${BACKEND_URL}${recording.url}`;
    console.log('Playing recording:', { filename: recording.filename, url: audioUrl });

    // Stop current playback
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = '';
      setAudioPlayer(null);
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';

    // Better error handling
    audio.onerror = () => {
      console.error('Audio playback error:', audio.error);
      let msg = 'Failed to play recording.';
      if (audio.error?.code === 4) {
        msg = 'Audio format not supported. Try downloading the file.';
      }
      toast.error(msg);
      setCurrentPlayingRecording(null);
      setAudioPlayer(null);
    };

    audio.oncanplaythrough = () => {
      audio.play().catch(e => {
        console.warn('Play interrupted (user interaction needed?):', e);
        toast.warn('Click Play again to start playback');
      });
    };

    audio.onended = () => {
      setCurrentPlayingRecording(null);
      setAudioPlayer(null);
    };

    audio.src = audioUrl;
    setAudioPlayer(audio);
    setCurrentPlayingRecording(recording.filename);

    // Attempt to play
    audio.load();
  };

  const stopRecording = () => {
    if (audioPlayer) {
      audioPlayer.pause();
      setAudioPlayer(null);
      setCurrentPlayingRecording(null);
    }
  };

  const downloadRecording = async (recording) => {
    if (!recording || !recording.url) {
      toast.error('Recording URL is not available');
      return;
    }

    const filename = recording.filename || 'recording.wav';

    try {
      // Construct download URL - append ?download=true for local API URLs
      let downloadUrl = recording.url;
      if (!downloadUrl.startsWith('http://') && !downloadUrl.startsWith('https://')) {
        // Local API URL - append download parameter
        downloadUrl = downloadUrl.includes('?')
          ? `${downloadUrl}&download=true`
          : `${downloadUrl}?download=true`;
        downloadUrl = `${BACKEND_URL}${downloadUrl}`;
      } else {
        // S3 URL - use as-is
        downloadUrl = downloadUrl;
      }

      // Fetch the file as blob to force download
      toast.info('Preparing download...');
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch recording');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`Download started: ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download recording');
    }
  };

  // Helper function to find matching recording for a call
  const findMatchingRecording = (call) => {
    if (!call || !recordings || recordings.length === 0) {
      return null;
    }

    // Try to match by recording filename if call has a recording field
    if (call.recording) {
      const matched = recordings.find(rec => rec.filename === call.recording);
      if (matched) return matched;
    }

    // Try to match by callSid in filename (recordings often have callSid in their filename)
    if (call.callSid) {
      const matched = recordings.find(rec =>
        rec.filename && rec.filename.includes(call.callSid)
      );
      if (matched) return matched;
    }

    // Try to match by callSid pattern (e.g., "call-1763449311" might match "call-1763449311.878.wav")
    if (call.callSid) {
      const callSidBase = call.callSid.replace(/^call-/, '').split('-')[0];
      const matched = recordings.find(rec =>
        rec.filename && rec.filename.includes(callSidBase)
      );
      if (matched) return matched;
    }

    return null;
  };

  const startManualCall = async () => {
    if (!phone || !script) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate language selection
    if (!selectedLanguage) {
      toast.error('Please select a language first');
      return;
    }

    // Validate AI configuration
    if (!aiConfig.stt_model || !aiConfig.llm_model || !aiConfig.tts_model || !aiConfig.voice) {
      toast.error('Please select all AI model options (STT, LLM, TTS, and Voice)');
      return;
    }

    console.log('🚀 MANUAL CALL - Data sent:', { phone, script, aiConfig });

    setLoading(true);
    setCallInProgress(null);
    setConversation([]);

    try {
      toast.info('🤖 Initiating manual call with Asterisk...');

      // Use backend URL from environment
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const userId = phone;

      const manualPayload = {
        callerId: phone,
        script,
        ai_config: {
          ...aiConfig,
          language: selectedLanguage, // Ensure language is included
          tts_speed: aiConfig.tts_speed || 1.0 // Include speech speed
        },
        background_ambiance: backgroundAmbiance,
        amd_config: amdConfig,
        manual_call: true
      };

      console.log('📞 Sending request to Asterisk:', `${BACKEND_URL}/api/call/${userId}`);
      console.log('📞 Data sent:', JSON.stringify(manualPayload, null, 2));

      // Call Asterisk backend directly with script + AI config
      const response = await axios.post(`${BACKEND_URL}/api/call/${userId}`, manualPayload);

      console.log('✅ Response received from Asterisk:', response.data);

      // Handle Asterisk backend response
      if (response.data.success || response.status === 200) {
        const callId = response.data.callId || response.data.id || `call_${Date.now()}`;
        setCallInProgress({
          id: callId,
          phone: phone,
          status: 'initiated',
          timestamp: new Date().toISOString()
        });
        toast.success(`✅ Call initiated successfully to ${phone}! ID: ${callId}`);

        // Refresh call list
        resetCalls();
        return;
      } else {
        toast.error(`❌ Call failed: ${response.data.message || 'Unknown error'}`);
        return;
      }

    } catch (error) {
      console.error('Error starting manual call:', error);
      toast.error('Error starting call: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const testVoice = async () => {
    try {
      if (!aiConfig.voice) {
        toast.error('Please select a voice first');
        return;
      }

      toast.info('🔊 Testing voice in progress...');

      // Select test text based on selected language
      const testText = selectedLanguage === 'en'
        ? 'Hello, this is a test of the selected voice for VoxiPro AI.'
        : 'Bonjour, ceci est un test de la voix sélectionnée pour VoxiPro AI.';

      console.log('🎤 Testing voice - TTS Model:', aiConfig.tts_model, 'Voice ID:', aiConfig.voice);
      console.log('🎤 Selected voice details:', availableVoices.find(v => v.id === aiConfig.voice));

      // Use TTS generate endpoint with voice_test use case for 1-minute cleanup
      const response = await apiService.tts.generate({
        text: testText,
        model: aiConfig.tts_model, // Pass TTS model to help backend route to correct service
        voice: aiConfig.voice,
        format: 'wav',
        use_case: 'voice_test' // Mark for 1-minute cleanup
      });

      console.log('🎤 TTS Response:', response.data);

      if (response.data.success) {
        // Create audio element to play TTS
        const audio = new Audio();
        const audioUrl = response.data.audio_url.startsWith('http')
          ? response.data.audio_url
          : `${BACKEND_URL}${response.data.audio_url}`;
        audio.src = audioUrl;

        audio.onloadeddata = () => {
          toast.success('🎵 Voice test successful!');
          audio.play();
        };

        audio.onerror = () => {
          toast.error('Error during audio playback');
        };

        audio.onended = () => {
          toast.info('🎵 Audio playback completed');
        };
      } else {
        toast.error(response.data.message || 'Error during voice test');
      }
    } catch (error) {
      console.error('Error testing voice:', error);
      toast.error('Error testing voice: ' + (error.response?.data?.message || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Manual Calls</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Call form */}
          <div ref={formContainerRef} className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">New Manual Call</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+33756834054"
                />
              </div>

              {/* SIP Provider Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SIP Provider *
                </label>
                <select
                  value={sipConfig.provider}
                  onChange={(e) => setSipConfig({ ...sipConfig, provider: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableSipProviders
                    .filter(provider => provider.enabled && provider.status === 'available')
                    .map(provider => (
                      <option key={provider.id} value={provider.id}>{provider.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Script *
                </label>
                <textarea
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type your script here..."
                />
              </div>

              {/* AI Configuration */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">AI Configuration</h3>

                {/* Language Selection - Must be selected first */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language *
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Language First...</option>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select language first to filter available models
                  </p>
                </div>

                {selectedLanguage && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        STT Model *
                      </label>
                      <select
                        value={aiConfig.stt_model}
                        onChange={(e) => setAiConfig({ ...aiConfig, stt_model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!selectedLanguage}
                      >
                        <option value="">Select STT Model...</option>
                        {getFilteredModels('stt')
                          .map(model => {
                            const isUp = model.status === 'running' || model.status === 'available';
                            return (
                              <option key={`${model.id}-${model.name}`} value={model.id} disabled={!isUp}>
                                {model.name}{isUp ? '' : ' (indisponible)'}
                              </option>
                            );
                          })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LLM Model *
                      </label>
                      <select
                        value={aiConfig.llm_model}
                        onChange={(e) => setAiConfig({ ...aiConfig, llm_model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!selectedLanguage}
                      >
                        <option value="">Select LLM Model...</option>
                        {getFilteredModels('llm')
                          .map(model => {
                            const isUp = model.status === 'running' || model.status === 'available';
                            return (
                              <option key={`${model.id}-${model.name}`} value={model.id} disabled={!isUp}>
                                {model.name}{isUp ? '' : ' (indisponible)'}
                              </option>
                            );
                          })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        TTS Model *
                      </label>
                      <select
                        value={aiConfig.tts_model}
                        onChange={(e) => setAiConfig({ ...aiConfig, tts_model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!selectedLanguage}
                      >
                        <option value="">Select TTS Model...</option>
                        {getFilteredModels('tts')
                          .map(model => {
                            const isUp = model.status === 'running' || model.status === 'available';
                            return (
                              <option key={`${model.id}-${model.name}`} value={model.id} disabled={!isUp}>
                                {model.name}{isUp ? '' : ' (indisponible)'}
                              </option>
                            );
                          })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI Voice *
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={aiConfig.voice}
                          onChange={(e) => setAiConfig({ ...aiConfig, voice: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={availableVoices.length === 0}
                        >
                          {availableVoices.length > 0 ? (
                            availableVoices.map(voice => (
                              <option key={voice.id} value={voice.id}>{voice.name || voice.id}</option>
                            ))
                          ) : (
                            <option value="">{aiConfig.tts_model ? 'Loading voices...' : 'Select a TTS model first'}</option>
                          )}
                        </select>
                        <button
                          onClick={testVoice}
                          disabled={!aiConfig.voice || availableVoices.length === 0}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🔊 Test
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* TTS Speed Control */}
                {aiConfig.tts_model && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Speech Speed: <span className="text-blue-600 font-semibold">{(aiConfig.tts_speed || 1.0).toFixed(2)}x</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">0.5x</span>
                      <input
                        type="range"
                        min="0.50"
                        max="1.50"
                        step="0.05"
                        value={aiConfig.tts_speed || 1.0}
                        onChange={(e) => setAiConfig({ ...aiConfig, tts_speed: parseFloat(e.target.value) })}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <span className="text-xs text-gray-500">1.5x</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Adjust voice speed — lower = slower/calmer, higher = faster
                    </p>
                  </div>
                )}
              </div>

              {/* System prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt
                </label>
                <textarea
                  value={aiConfig.system_prompt}
                  onChange={(e) => setAiConfig({ ...aiConfig, system_prompt: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Instructions for AI..."
                />
              </div>

              {/* Sound Ambiance */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">🎵 Sound Ambiance</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Ambiance
                  </label>
                  <select
                    value={backgroundAmbiance}
                    onChange={(e) => setBackgroundAmbiance(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {availableAmbiances.map(ambiance => (
                      <option key={ambiance.id} value={ambiance.id}>
                        {ambiance.name} - {ambiance.description}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    The ambiance will be mixed at 20% of the TTS volume for a realistic environment.
                  </p>
                </div>
              </div>

              {/* AMD Configuration */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">🎯 Answering Machine Detection (AMD)</h3>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={amdConfig.enabled}
                          onChange={(e) => setAmdConfig({
                            ...amdConfig,
                            enabled: e.target.checked
                          })}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Enable answering machine detection</span>
                          <p className="text-xs text-gray-600">Avoid unnecessary costs by automatically detecting answering machines</p>
                        </div>
                      </label>
                    </div>

                    {amdConfig.enabled && (
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">⚙️ AMD Settings (Optimized for France)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Initial silence (ms)
                            </label>
                            <input
                              type="number"
                              min="1000"
                              max="5000"
                              value={amdConfig.initial_silence}
                              onChange={(e) => setAmdConfig({
                                ...amdConfig,
                                initial_silence: parseInt(e.target.value) || 2250
                              })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500">Optimized for French operators</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Greeting message (ms)
                            </label>
                            <input
                              type="number"
                              min="500"
                              max="3000"
                              value={amdConfig.greeting}
                              onChange={(e) => setAmdConfig({
                                ...amdConfig,
                                greeting: parseInt(e.target.value) || 1800
                              })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Max analysis time (ms)
                            </label>
                            <input
                              type="number"
                              min="3000"
                              max="10000"
                              value={amdConfig.total_analysis_time}
                              onChange={(e) => setAmdConfig({
                                ...amdConfig,
                                total_analysis_time: parseInt(e.target.value) || 6000
                              })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-xs text-yellow-800">
                            ⚠️ <strong>Important:</strong> AMD adds 5-6s per call and generates 15-25% false positives depending on operator.
                            Mixed mode (SIP signaling + voice analysis) recommended for 99.9% accuracy.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Call button */}
              <div className="pt-4">
                <button
                  onClick={startManualCall}
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '⏳ Appel en cours...' : '📞 Lancer l\'appel Manuel'}
                </button>
              </div>
            </div>
          </div>

          {/* Call History */}
          <div
            ref={callHistoryRef}
            className="bg-white rounded-xl shadow-lg p-6 flex flex-col"
            style={formHeight ? { height: `${formHeight}px` } : {}}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-lg font-bold">Call History</h3>
              <button
                onClick={loadCalls}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <VirtualCallList
                calls={calls}
                onCallClick={setSelectedCall}
                height={600}
                itemHeight={120}
                hasMore={hasMoreCalls}
                loading={loadingCalls}
                observerTarget={callsObserverTarget}
              />
            </div>
          </div>
        </div>

        {/* Modal d'appel en cours */}
        {showCallModal && callInProgress && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Appel en Cours</h2>
                <button
                  onClick={() => {
                    setShowCallModal(false);
                    stopConversationPolling();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations de l'appel */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-3">Informations de l'appel</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Téléphone:</strong> {callInProgress.phone}</p>
                    <p><strong>Statut:</strong> {callInProgress.status}</p>
                    <p><strong>Durée:</strong> {callInProgress.duration || 0}s</p>
                    <p><strong>Pipeline:</strong> STT→LLM→TTS</p>
                  </div>

                  {/* Contrôles */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Connexion WebSocket</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-sm text-green-600">{connectionStatus}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => setIsRecording(!isRecording)}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isRecording
                        ? 'bg-red-500 text-white'
                        : 'bg-green-500 text-white'
                        }`}
                    >
                      <span className="text-xl">🎤</span>
                      <span>{isRecording ? 'Arrêter l\'enregistrement' : 'Démarrer l\'enregistrement'}</span>
                    </button>
                  </div>
                </div>

                {/* Conversation temps réel */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-3">Conversation Temps Réel</h3>
                  <div className="bg-white rounded-lg p-3 h-64 overflow-y-auto mb-3">
                    {conversation.length > 0 ? (
                      conversation.map((message, index) => (
                        <div key={`${message.type}-${message.timestamp}-${index}`} className={`mb-3 ${message.type === 'ia' ? 'text-left' : 'text-right'}`}>
                          <div className={`inline-block px-3 py-2 rounded-lg ${message.type === 'ia'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}>
                            <p className="text-sm font-medium">{message.type === 'ia' ? 'IA' : 'Prospect'}</p>
                            <p className="text-sm">{message.text}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {message.timestamp.toLocaleTimeString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        En attente de la conversation...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call Details Modal */}
        {selectedCall && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Call Details</h2>
                <button
                  onClick={() => setSelectedCall(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">From</label>
                    <p className="text-gray-900">{selectedCall.caller || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">To</label>
                    <p className="text-gray-900">{selectedCall.receiver || selectedCall.phone || selectedCall.to || 'Unknown'}</p>
                  </div>
                </div>

                {selectedCall.callSid && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Call SID</label>
                    <p className="text-gray-900 font-mono text-sm">{selectedCall.callSid}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Date/Time</label>
                  <p className="text-gray-900">{new Date(selectedCall.createdAt || selectedCall.created_at).toLocaleString('en-US')}</p>
                </div>

                {selectedCall.summary && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Summary</label>
                    <div className="bg-gray-50 rounded-lg p-3 mt-1">
                      <p className="text-sm text-gray-700">{selectedCall.summary}</p>
                    </div>
                  </div>
                )}

                {selectedCall.transcript && selectedCall.transcript.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Conversation Transcript</label>
                    <div className="bg-gray-50 rounded-lg p-3 mt-1 max-h-64 overflow-y-auto">
                      {selectedCall.transcript.map((message, index) => (
                        <div key={index} className={`mb-2 ${message.role === 'agent' ? 'text-left' : 'text-right'}`}>
                          <div className={`inline-block px-3 py-2 rounded-lg ${message.role === 'agent'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}>
                            <p className="text-xs font-medium mb-1">
                              {message.role === 'agent' ? 'Agent' : 'User'}
                            </p>
                            <p className="text-sm">{message.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(() => {
                  // Check if call has a completed recording
                  const hasCompletedRecording = selectedCall.recording_status === 'completed' || selectedCall.recording

                  if (!hasCompletedRecording) return null;

                  const matchingRecording = findMatchingRecording(selectedCall);

                  // If we have a matching recording, use it; otherwise create a recording object from recordingUrl
                  let recordingToPlay = null;

                  if (matchingRecording) {
                    recordingToPlay = matchingRecording;
                  } else if (selectedCall.recording) {
                    // Extract filename from full path if needed
                    const recordingPath = selectedCall.recording.trim();
                    // Get just the filename (part after last slash)
                    const filename = recordingPath.includes('/')
                      ? recordingPath.substring(recordingPath.lastIndexOf('/') + 1)
                      : recordingPath;

                    // If we have a recording filename but no matching recording, construct URL from filename
                    recordingToPlay = {
                      filename: filename,
                      url: `/api/recordings/${filename}`
                    };
                  } else if (selectedCall.recording && selectedCall.recording.trim()) {
                    const url = selectedCall.recording.trim();
                    let constructedUrl = '';
                    let filename = '';

                    // If it's a full HTTP/HTTPS URL, extract the path
                    if (url.startsWith('http://') || url.startsWith('https://')) {
                      try {
                        const urlObj = new URL(url);
                        constructedUrl = urlObj.pathname;
                        // Extract filename from pathname
                        filename = constructedUrl.substring(constructedUrl.lastIndexOf('/') + 1);
                      } catch (e) {
                        console.warn('Failed to parse URL:', url, e);
                        // If URL parsing fails, try to extract path manually
                        const backendUrlMatch = url.indexOf(BACKEND_URL);
                        if (backendUrlMatch !== -1) {
                          constructedUrl = url.substring(backendUrlMatch + BACKEND_URL.length);
                          filename = constructedUrl.substring(constructedUrl.lastIndexOf('/') + 1);
                        } else {
                          // Fallback: assume it's just the path
                          const pathMatch = url.match(/\/api\/[^?]*/);
                          constructedUrl = pathMatch ? pathMatch[0] : url;
                          filename = constructedUrl.substring(constructedUrl.lastIndexOf('/') + 1);
                        }
                      }
                    } else if (url.startsWith('/api/')) {
                      // If it's already an API path, use it as is
                      constructedUrl = url;
                      filename = url.substring(url.lastIndexOf('/') + 1);
                    } else if (url.startsWith('/')) {
                      // If it starts with / but is not /api/, it's likely a filesystem path
                      // Extract filename and construct API URL
                      filename = url.substring(url.lastIndexOf('/') + 1);
                      constructedUrl = `/api/recordings/${filename}`;
                    } else {
                      // Otherwise, assume it's a filename and construct the path
                      filename = url;
                      constructedUrl = `/api/recordings/${filename}`;
                    }

                    // Ensure the URL is valid
                    if (!constructedUrl || !constructedUrl.startsWith('/')) {
                      console.error('Invalid recording URL constructed:', constructedUrl);
                      return null;
                    }

                    recordingToPlay = {
                      filename: filename || selectedCall.callSid || selectedCall._id || 'recording',
                      url: constructedUrl
                    };
                  }

                  if (!recordingToPlay || !recordingToPlay.url) {
                    return null;
                  }

                  const isPlaying = currentPlayingRecording === recordingToPlay.filename;

                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Recording</label>
                      <div className="mt-1 space-y-2">
                        <button
                          onClick={() => {
                            if (isPlaying) {
                              stopRecording();
                            } else {
                              playRecording(recordingToPlay);
                            }
                          }}
                          className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isPlaying
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                        >
                          <span className="text-lg">
                            {isPlaying ? '⏹️' : '▶️'}
                          </span>
                          <span className="text-sm">
                            {isPlaying ? 'Stop' : 'Play Recording'}
                          </span>
                        </button>
                        <button
                          onClick={() => downloadRecording(recordingToPlay)}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700"
                        >
                          <span className="text-lg">📥</span>
                          <span className="text-sm">Download Recording</span>
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedCall(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplifiedManualCalls;